import { PPL_COUNTRY_CODES } from 'shared-utils/ppl-countries';
import { LEGAL_PAGE_ROUTES } from './constants';

const MONETIZATION_TYPES = {
  PPC: 'PPC',
  PPL: 'PPL',
  Null: 'Null',
};

const PPL_MONETIZATION_TYPES = {
  Chat: 'Chat',
  FormCTA: 'Form CTA',
  FormExitIntent: 'Form exit intent',
  NA: 'Not applicable',
};

const PPL_COUNTRIES = new Set(PPL_COUNTRY_CODES);

const ALWAYS_NULL_ROUTES = new Set([
  ...LEGAL_PAGE_ROUTES,
  'category_leaders_methodology',
  'data_protection_agreement',
  'data_processing_addendum',
  'data_transfer_addendum',
  'free_stuff_addendum',
  'ppc_service_description',
  'ppl_service_description',
]);

const AGNOSTIC_PPL_ROUTES = new Set([
  'about',
  'browse',
  'content_hub',
  'home',
  'press',
  'resources-all',
  'resources_author',
  'resources_home',
  'resources_post',
  'resources_topic',
  'topics_home',
]);

const CATEGORY_ROUTES = new Set([
  'sem_serp',
  'serp_category',
  'serp_fulltext',
  'serp_integrated_with',
  'serp_oses',
  'serp_pricingcategories',
  'serp_tag',
  'serp_typecustomers',
]);

const PRODUCT_ROUTES = new Set([
  'alternatives',
  'compare',
  'integrations',
  'listing_detail',
  'listing_features',
  'pricing',
  'reviews_listing',
  'sem_listing_detail',
]);

const getFirstDataLayerEntry = (head) =>
  head?.data_layer?.find((entry) => entry && typeof entry === 'object') || {};

const getCountryCode = ({ countryCode, dataLayer }) =>
  dataLayer.geoipCountryCode || countryCode;

const getMonetizationTypeFromFlags = ({ hasPpc, hasPpl }) => {
  const values = [];
  if (hasPpc) values.push(MONETIZATION_TYPES.PPC);
  if (hasPpl) values.push(MONETIZATION_TYPES.PPL);
  return values.length > 0 ? values.join(',') : MONETIZATION_TYPES.Null;
};

const getPplMonetizationTypeFromFlags = ({
  hasChat,
  hasFormCTA,
  hasFormExitIntent,
}) => {
  const values = [];
  if (hasChat) values.push(PPL_MONETIZATION_TYPES.Chat);
  if (hasFormCTA) values.push(PPL_MONETIZATION_TYPES.FormCTA);
  if (hasFormExitIntent) values.push(PPL_MONETIZATION_TYPES.FormExitIntent);
  return values.length > 0 ? values.join(',') : PPL_MONETIZATION_TYPES.NA;
};

const hasUpgradedPpcStatus = (dataLayer) =>
  ['Upgraded', 'PPC'].includes(
    dataLayer.ppcMonetizationStatus || dataLayer.ppc_monetization_status
  );

const getHasPpc = ({ route, dataLayer }) => {
  if (hasUpgradedPpcStatus(dataLayer)) return true;
  if (route === 'sem_listing_detail') return true;
  return CATEGORY_ROUTES.has(route);
};

const getHasPpl = ({ head, route, isPplCountry }) => {
  if (!isPplCountry || ALWAYS_NULL_ROUTES.has(route)) return false;

  if (
    head?.hasPplCategory ||
    head?.hasPplDemoFormEnabled ||
    head?.hasPplExitIntentEnabled ||
    head?.hasPplExpertFormEnabled ||
    head?.hasLiveChatEnabled
  ) {
    return true;
  }

  return AGNOSTIC_PPL_ROUTES.has(route);
};

export const getMonetizationDataLayerFields = ({
  head = {},
  countryCode,
  route,
}) => {
  const dataLayer = getFirstDataLayerEntry(head);
  const resolvedRoute = route || dataLayer.routeName;

  if (!resolvedRoute) {
    return {
      monetization_type: MONETIZATION_TYPES.Null,
      ppl_monetization_type: PPL_MONETIZATION_TYPES.NA,
    };
  }

  const resolvedCountryCode = getCountryCode({ countryCode, dataLayer });
  const isPplCountry = PPL_COUNTRIES.has(resolvedCountryCode);
  const hasPpl = getHasPpl({ head, route: resolvedRoute, isPplCountry });
  const hasPpc = getHasPpc({ route: resolvedRoute, dataLayer });
  const isProductRoute = PRODUCT_ROUTES.has(resolvedRoute);
  const isCategoryRoute = CATEGORY_ROUTES.has(resolvedRoute);

  return {
    monetization_type: getMonetizationTypeFromFlags({ hasPpc, hasPpl }),
    ppl_monetization_type: getPplMonetizationTypeFromFlags({
      hasChat: hasPpl && Boolean(head?.hasLiveChatEnabled),
      hasFormCTA:
        hasPpl &&
        (Boolean(head?.hasPplDemoFormEnabled) ||
          Boolean(head?.hasPplExpertFormEnabled) ||
          (isProductRoute && Boolean(head?.hasPplCategory))),
      hasFormExitIntent:
        hasPpl && (Boolean(head?.hasPplExitIntentEnabled) || isCategoryRoute),
    }),
  };
};
